import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import {
  Dashboard,
  TrendingUp,
  AttachMoney,
  FilterAlt,
  Storefront,
  BarChart,
  Group,
  PeopleAlt,
  Receipt,
  PersonAdd,
  Leaderboard,
  AdminPanelSettings,
  Category,
  AccountBalance,
  HourglassEmpty,
  CheckCircle,
  PendingActions,
  ErrorOutline,
  ExpandMore,
  ExpandLess,
  Logout,
  AutoStories,
  Instagram,
  WorkspacePremium,
  AutoAwesome,
  PhotoLibrary,
  Forum,
  Mail,
  Insights,
  Collections,
  Flag,
  Autorenew,
} from "@mui/icons-material";
import { useAuth } from "../../AuthContext";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SidebarContainer = styled.aside`
  width: 240px;
  min-width: 240px;
  height: 100vh;
  background: linear-gradient(180deg, #0a0f1c 0%, #111827 100%);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  border-right: 1px solid rgba(148, 163, 184, 0.08);
`;

const LogoSection = styled.div`
  padding: 20px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
  flex-shrink: 0;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  flex-shrink: 0;
`;

const LogoText = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: #f8fafc;
  letter-spacing: -0.5px;
`;

const NavSection = styled.nav`
  flex: 1;
  padding: 12px 10px;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 10px 8px 6px;
`;

const NavGroup = styled.div`
  margin-bottom: 2px;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  color: ${(props) => (props.$isActive ? "#f8fafc" : "#94a3b8")};
  text-decoration: none;
  font-size: 13px;
  font-weight: ${(props) => (props.$isActive ? "500" : "400")};
  background: ${(props) =>
    props.$isActive
      ? "linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(139,92,246,0.15) 100%)"
      : "transparent"};
  transition: all 0.15s ease;
  position: relative;

  ${(props) =>
    props.$isActive &&
    css`
      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 20px;
        background: linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%);
        border-radius: 0 4px 4px 0;
      }
    `}

  &:hover {
    background: ${(props) =>
      props.$isActive
        ? "linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(139,92,246,0.2) 100%)"
        : "rgba(148,163,184,0.08)"};
    color: #f8fafc;
  }

  svg {
    font-size: 17px;
    opacity: ${(props) => (props.$isActive ? 1 : 0.65)};
    flex-shrink: 0;
  }
`;

const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 10px;
  border-radius: 8px;
  color: ${(props) => (props.$isActive ? "#f8fafc" : "#94a3b8")};
  background: ${(props) =>
    props.$isActive
      ? "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.1) 100%)"
      : "transparent"};
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(props) => (props.$isActive ? "500" : "400")};
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover {
    background: rgba(148, 163, 184, 0.08);
    color: #f8fafc;
  }
`;

const CollapsibleHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  svg {
    font-size: 17px;
    opacity: 0.65;
  }
`;

const SubMenu = styled.div`
  margin-left: 16px;
  padding-left: 12px;
  border-left: 1px solid rgba(148, 163, 184, 0.1);
  margin-top: 2px;
  overflow: hidden;
  max-height: ${(props) => (props.$isOpen ? "300px" : "0")};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: all 0.25s ease;
`;

const SubMenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 7px;
  color: ${(props) => (props.$isActive ? "#f8fafc" : "#64748b")};
  text-decoration: none;
  font-size: 12px;
  font-weight: ${(props) => (props.$isActive ? "500" : "400")};
  background: ${(props) =>
    props.$isActive ? "rgba(99,102,241,0.15)" : "transparent"};
  transition: all 0.15s ease;
  animation: ${fadeIn} 0.2s ease forwards;
  animation-delay: ${(props) => props.$delay || 0}ms;

  &:hover {
    background: rgba(148, 163, 184, 0.08);
    color: #f8fafc;
  }

  svg {
    font-size: 14px;
    opacity: 0.6;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(148, 163, 184, 0.08);
  margin: 8px 8px;
`;

const BottomSection = styled.div`
  padding: 12px 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.08);
  flex-shrink: 0;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.05);
  margin-bottom: 8px;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  color: white;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const UserName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #f8fafc;
  truncate: true;
`;
const UserRole = styled.div`
  font-size: 10px;
  color: #64748b;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border-radius: 7px;
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.5);
  }

  svg {
    font-size: 16px;
  }
`;

const ANALYTICS_NAV = [
  {
    path: "/analytics/overview",
    label: "Overview",
    icon: <Dashboard />,
    feature: "overview",
  },
  {
    path: "/analytics/gmv",
    label: "GMV",
    icon: <AttachMoney />,
    feature: "gmv",
  },
  {
    path: "/analytics/revenue",
    label: "Revenue",
    icon: <TrendingUp />,
    feature: "revenue",
  },
  {
    path: "/analytics/funnel",
    label: "Creator Funnel",
    icon: <FilterAlt />,
    feature: "funnel",
  },
  {
    path: "/analytics/brand-pages",
    label: "Brand Pages",
    icon: <Storefront />,
    feature: "brand-pages",
  },
  {
    path: "/analytics/users",
    label: "Users",
    icon: <PeopleAlt />,
    feature: "users",
  },
  {
    path: "/analytics/settlements",
    label: "Settlement Stats",
    icon: <BarChart />,
    feature: "settlements",
  },
  {
    path: "/analytics/customers",
    label: "Customers",
    icon: <Group />,
    feature: "customers",
  },
  {
    path: "/analytics/transactions",
    label: "Transactions",
    icon: <Receipt />,
    feature: "transactions",
  },
  // "Leads" moved out of Analytics into its own top-level "Leads" group (see below).
  {
    path: "/analytics/new-registrations",
    label: "New Registrations",
    icon: <PersonAdd />,
    feature: "new-registrations",
  },
  {
    path: "/analytics/saas-plans",
    label: "SaaS Plans",
    icon: <WorkspacePremium />,
    feature: "saas-plans",
  },
  {
    path: "/analytics/insta-plans",
    label: "Insta Auto Plans",
    icon: <AutoAwesome />,
    feature: "insta-plans",
  },
  {
    path: "/analytics/seo-blogs",
    label: "SEO Blogs",
    icon: <AutoStories />,
    feature: "seo-blogs",
  },
];

function Sidebar() {
  const location = useLocation();
  const { logout, adminUser } = useAuth();
  const [settlementsOpen, setSettlementsOpen] = useState(false);
  const [leadsOpen, setLeadsOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const isInGroup = (paths) =>
    paths.some((p) => location.pathname.startsWith(p));

  // Auto-expand the relevant submenu when navigated into one of its routes
  React.useEffect(() => {
    if (location.pathname.startsWith("/settlements")) {
      setSettlementsOpen(true);
    }
    if (
      location.pathname.startsWith("/nurture") ||
      location.pathname.startsWith("/crm") ||
      location.pathname === "/analytics/leads"
    ) {
      setLeadsOpen(true);
    }
  }, [location.pathname]);

  return (
    <SidebarContainer>
      <LogoSection>
        <LogoWrapper>
          <LogoIcon>G</LogoIcon>
          <LogoText>Growezy</LogoText>
        </LogoWrapper>
      </LogoSection>

      <NavSection>
        {/* ── Founder Goals (top — north-star context for everything below) ── */}
        <SectionLabel>Strategy</SectionLabel>
        <NavGroup>
          <NavItem to="/goals" $isActive={isActive("/goals")}>
            <Flag />
            <span>Goals</span>
          </NavItem>
        </NavGroup>
        <Divider />

        {/* ── CRM (moved to top — primary daily-driver surface) ────────────
            Sales Funnel: pipeline + WhatsApp nurture + per-category images. */}
        {(adminUser?.role === "superadmin" ||
          adminUser?.allowedFeatures?.includes("leads") ||
          adminUser?.allowedFeatures?.includes("nurture")) && (
          <>
            <SectionLabel>CRM</SectionLabel>
            <NavGroup>
              <CollapsibleHeader
                onClick={() => setLeadsOpen((o) => !o)}
                $isActive={isInGroup(["/nurture", "/analytics/leads"])}
              >
                <CollapsibleHeaderContent>
                  <Leaderboard />
                  <span>Sales Funnel</span>
                </CollapsibleHeaderContent>
                {leadsOpen ? (
                  <ExpandLess style={{ fontSize: 16 }} />
                ) : (
                  <ExpandMore style={{ fontSize: 16 }} />
                )}
              </CollapsibleHeader>
              <SubMenu $isOpen={leadsOpen}>
                <SubMenuItem
                  to="/crm/dashboard"
                  $isActive={isActive("/crm/dashboard")}
                  $delay={0}
                >
                  <Dashboard />
                  <span>Dashboard</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/crm/pre-onboard"
                  $isActive={isActive("/crm/pre-onboard")}
                  $delay={30}
                >
                  <FilterAlt />
                  <span>Pre-Onboard Board</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/crm/post-onboard"
                  $isActive={isActive("/crm/post-onboard")}
                  $delay={60}
                >
                  <WorkspacePremium />
                  <span>Post-Onboard Board</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/analytics/leads"
                  $isActive={isActive("/analytics/leads")}
                  $delay={90}
                >
                  <Leaderboard />
                  <span>All Contacts (Table)</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/nurture/templates"
                  $isActive={isActive("/nurture/templates")}
                  $delay={120}
                >
                  <Mail />
                  <span>WA Templates</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/nurture/category-images"
                  $isActive={isActive("/nurture/category-images")}
                  $delay={140}
                >
                  <Collections />
                  <span>Category Images</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/nurture/messages"
                  $isActive={isActive("/nurture/messages")}
                  $delay={150}
                >
                  <Forum />
                  <span>WA Messages</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/nurture/analytics"
                  $isActive={isActive("/nurture/analytics")}
                  $delay={180}
                >
                  <Insights />
                  <span>Nurture Analytics</span>
                </SubMenuItem>
              </SubMenu>
            </NavGroup>
            <Divider />
          </>
        )}

        {/* Analytics */}
        <SectionLabel>Analytics</SectionLabel>
        {ANALYTICS_NAV.filter((item) => {
          if (adminUser?.role === "superadmin") return true;
          return adminUser?.allowedFeatures?.includes(item.feature);
        }).map((item) => (
          <NavGroup key={item.path}>
            <NavItem to={item.path} $isActive={isActive(item.path)}>
              {item.icon}
              <span>{item.label}</span>
            </NavItem>
          </NavGroup>
        ))}

        {(adminUser?.role === "superadmin" ||
          adminUser?.allowedFeatures?.includes("settlement-ops")) && (
          <>
            <Divider />
            <SectionLabel>Operations</SectionLabel>
            <NavGroup>
              <CollapsibleHeader
                onClick={() => setSettlementsOpen((o) => !o)}
                $isActive={isInGroup(["/settlements"])}
              >
                <CollapsibleHeaderContent>
                  <AccountBalance />
                  <span>Settlements</span>
                </CollapsibleHeaderContent>
                {settlementsOpen ? (
                  <ExpandLess style={{ fontSize: 16 }} />
                ) : (
                  <ExpandMore style={{ fontSize: 16 }} />
                )}
              </CollapsibleHeader>
              <SubMenu $isOpen={settlementsOpen}>
                <SubMenuItem
                  to="/settlements/pending"
                  $isActive={isActive("/settlements/pending")}
                  $delay={0}
                >
                  <HourglassEmpty />
                  <span>Pending</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/settlements/ready"
                  $isActive={isActive("/settlements/ready")}
                  $delay={40}
                >
                  <PendingActions />
                  <span>Ready to Process</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/settlements/failed"
                  $isActive={isActive("/settlements/failed")}
                  $delay={80}
                >
                  <ErrorOutline />
                  <span>Failed</span>
                </SubMenuItem>
                <SubMenuItem
                  to="/settlements/settled"
                  $isActive={isActive("/settlements/settled")}
                  $delay={120}
                >
                  <CheckCircle />
                  <span>Settled</span>
                </SubMenuItem>
              </SubMenu>
            </NavGroup>
          </>
        )}

        {adminUser?.role === "superadmin" && (
          <>
            <Divider />
            <SectionLabel>Admin</SectionLabel>
            <NavGroup>
              <NavItem
                to="/category-pages"
                $isActive={isInGroup(["/category-pages"])}
              >
                <Category />
                <span>Category Pages</span>
              </NavItem>
            </NavGroup>
            <NavGroup>
              <NavItem to="/admin-users" $isActive={isActive("/admin-users")}>
                <AdminPanelSettings />
                <span>Admin Users</span>
              </NavItem>
            </NavGroup>
            <NavGroup>
              <NavItem to="/photo-cloud" $isActive={isActive("/photo-cloud")}>
                <PhotoLibrary />
                <span>Photo Cloud</span>
              </NavItem>
            </NavGroup>
            <NavGroup>
              <NavItem
                to="/analytics/update-instagram-details"
                $isActive={isActive("/analytics/update-instagram-details")}
              >
                <Instagram />
                <span>Update Instagram Details</span>
              </NavItem>
            </NavGroup>
          </>
        )}

        {/* ── Renewal Reminders (plan-expiry WA messages: settings + templates) ─ */}
        {(adminUser?.role === "superadmin" ||
          adminUser?.allowedFeatures?.includes("nurture")) && (
          <>
            <Divider />
            <SectionLabel>Subscriptions</SectionLabel>
            <NavGroup>
              <NavItem
                to="/renewal-reminders"
                $isActive={isActive("/renewal-reminders")}
              >
                <Autorenew />
                <span>Renewal Reminders</span>
              </NavItem>
            </NavGroup>
          </>
        )}
      </NavSection>

      <BottomSection>
        <UserSection>
          {adminUser?.photoURL ? (
            <img
              src={adminUser.photoURL}
              alt=""
              style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }}
              referrerPolicy="no-referrer"
            />
          ) : (
            <UserAvatar>
              {(adminUser?.name || "A").charAt(0).toUpperCase()}
            </UserAvatar>
          )}
          <UserInfo>
            <UserName>{adminUser?.name || "Admin"}</UserName>
            <UserRole>{adminUser?.role || "Administrator"}</UserRole>
          </UserInfo>
        </UserSection>
        <LogoutButton onClick={logout}>
          <Logout />
          <span>Logout</span>
        </LogoutButton>
      </BottomSection>
    </SidebarContainer>
  );
}

export default Sidebar;
